import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Staff roles enum (for display/UI purposes only)
 * Authorization uses per-marina roles from Init API cache (UserMarinaRole table), not this enum.
 */
export enum StaffRole {
  ADMIN = 0,
  DOCK_MASTER = 1,
  OFFICE_ADMIN = 2,
  SERVICE_MANAGER = 3,
  SERVICE_TECH = 4,
  DECK_HAND = 5,
}

/**
 * Service for checking user permissions and roles.
 * Uses cached Init API data for authorization checks (NOT JWT claims).
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  constructor(private authService: AuthService) {}

  /**
   * Check if user is system admin
   * Uses cached Init API data (NOT JWT claim)
   */
  isSystemAdmin(): boolean {
    return this.authService.isSystemAdmin();
  }

  /**
   * Check if user has a specific role in a marina
   * Uses cached Init API data (from UserMarinaRole table)
   */
  hasMarinaRole(marinaId: string, role: string): boolean {
    const roles = this.authService.getRolesForMarina(marinaId);
    return roles.some((r) => r.toUpperCase() === role.toUpperCase());
  }

  /**
   * Check if user has a specific role in the current marina
   * Convenience method that uses currently selected marina
   */
  hasRoleInCurrentMarina(role: string): boolean {
    const currentMarinaId = this.authService.getCurrentMarinaId();
    if (!currentMarinaId) {
      return false;
    }
    return this.hasMarinaRole(currentMarinaId, role);
  }

  /**
   * Check if user has any of the specified roles in a marina
   */
  hasAnyMarinaRole(marinaId: string, ...roles: string[]): boolean {
    return roles.some((role) => this.hasMarinaRole(marinaId, role));
  }

  /**
   * Check if user has any of the specified roles in the current marina
   */
  hasAnyRoleInCurrentMarina(...roles: string[]): boolean {
    const currentMarinaId = this.authService.getCurrentMarinaId();
    if (!currentMarinaId) {
      return false;
    }
    return this.hasAnyMarinaRole(currentMarinaId, ...roles);
  }

  /**
   * Check if user has a global Identity Role (optional - only for system-wide roles)
   * Note: Per-marina staff roles are stored in UserMarinaRole table, not Identity Roles
   */
  hasGlobalRole(role: string): boolean {
    // TODO: Implement if using Identity Roles for global roles
    // For now, only system admin is a global role
    if (role.toUpperCase() === 'SYSTEM_ADMIN') {
      return this.isSystemAdmin();
    }
    return false;
  }

  /**
   * Check if user has a role (for display/UI purposes only)
   * Uses StaffRole enum for display, but authorization should use hasMarinaRole()
   */
  hasRole(role: StaffRole | string): boolean {
    // This is for UI/display purposes only
    // For authorization, use hasMarinaRole() instead
    const user = this.authService.currentUserValue;
    if (!user || !user.staffRole) {
      return false;
    }
    if (typeof role === 'number') {
      return user.staffRole === role;
    }
    // String comparison (e.g., "ADMIN")
    const roleMap: Record<string, number> = {
      ADMIN: StaffRole.ADMIN,
      DOCK_MASTER: StaffRole.DOCK_MASTER,
      OFFICE_ADMIN: StaffRole.OFFICE_ADMIN,
      SERVICE_MANAGER: StaffRole.SERVICE_MANAGER,
      SERVICE_TECH: StaffRole.SERVICE_TECH,
      DECK_HAND: StaffRole.DECK_HAND,
    };
    return user.staffRole === roleMap[role.toUpperCase()];
  }

  /**
   * Check if user has a permission (placeholder for future permission system)
   */
  hasPermission(permission: string): boolean {
    // TODO: Implement permission-based checks if needed
    return false;
  }

  /**
   * Check if user can access a feature
   */
  canAccessFeature(feature: string): boolean {
    // System admin can access all features
    if (this.isSystemAdmin()) {
      return true;
    }

    // TODO: Implement feature-based access control
    return false;
  }

  /**
   * Get accessible marinas
   * Returns all marinas if system admin
   */
  getAccessibleMarinas() {
    return this.authService.getAccessibleMarinas();
  }

  /**
   * Check if user can access a marina
   * Always returns true for system admin
   */
  canAccessMarina(marinaId: string): boolean {
    // System admin can access all marinas
    if (this.isSystemAdmin()) {
      return true;
    }

    // Check if marina is in accessible marinas list
    const accessibleMarinas = this.authService.getAccessibleMarinas();
    return accessibleMarinas.some((m) => m.id === marinaId);
  }

  /**
   * Check if user can manage all users
   * Returns true for system admin
   */
  canManageAllUsers(): boolean {
    return this.isSystemAdmin();
  }

  /**
   * Check if user can manage all marinas
   * Returns true for system admin
   */
  canManageAllMarinas(): boolean {
    return this.isSystemAdmin();
  }

  /**
   * Check if user can manage leases (requires ADMIN or DOCK_MASTER role in current marina)
   */
  canManageLeases(): boolean {
    return this.hasAnyRoleInCurrentMarina('ADMIN', 'DOCK_MASTER');
  }

  /**
   * Check if user can manage payments (requires ADMIN, OFFICE_ADMIN, or SERVICE_MANAGER role in current marina)
   */
  canManagePayments(): boolean {
    return this.hasAnyRoleInCurrentMarina('ADMIN', 'OFFICE_ADMIN', 'SERVICE_MANAGER');
  }

  /**
   * Check if user can view reports (requires ADMIN, OFFICE_ADMIN, or SERVICE_MANAGER role in current marina)
   */
  canViewReports(): boolean {
    return this.hasAnyRoleInCurrentMarina('ADMIN', 'OFFICE_ADMIN', 'SERVICE_MANAGER');
  }

  /**
   * Service manager without ADMIN / DOCK_MASTER / OFFICE_ADMIN in the current marina — reduced nav and dashboard.
   */
  isServiceManagerOnlyInCurrentMarina(): boolean {
    if (this.isSystemAdmin()) {
      return false;
    }
    const marinaId = this.authService.getCurrentMarinaId();
    if (!marinaId) {
      return false;
    }
    if (this.hasAnyMarinaRole(marinaId, 'ADMIN', 'DOCK_MASTER', 'OFFICE_ADMIN')) {
      return false;
    }
    return this.hasMarinaRole(marinaId, 'SERVICE_MANAGER');
  }

  /**
   * Matches API policy LeaseContractManagement — callers must skip lease/contract HTTP endpoints when false
   * so 403 does not trigger the global ErrorInterceptor redirect.
   */
  canAccessLeaseContractApiForMarina(marinaId: string): boolean {
    if (this.isSystemAdmin()) {
      return true;
    }
    return this.hasAnyMarinaRole(
      marinaId,
      'ADMIN',
      'DOCK_MASTER',
      'OFFICE_ADMIN',
      'SERVICE_TECH'
    );
  }

  canAccessLeaseContractApiInCurrentMarina(): boolean {
    const marinaId = this.authService.getCurrentMarinaId();
    return marinaId ? this.canAccessLeaseContractApiForMarina(marinaId) : false;
  }

  /** Matches API policy AuditLogAccess. */
  canAccessAuditLogApiForMarina(marinaId: string): boolean {
    if (this.isSystemAdmin()) {
      return true;
    }
    return this.hasAnyMarinaRole(marinaId, 'ADMIN', 'DOCK_MASTER');
  }

  canAccessAuditLogApiInCurrentMarina(): boolean {
    const marinaId = this.authService.getCurrentMarinaId();
    return marinaId ? this.canAccessAuditLogApiForMarina(marinaId) : false;
  }
}
